'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {RefreshCw, Search, Users} from 'lucide-react'
import type {Account, Contact} from '@/types'
import {apiPost} from '@/lib/api'
import {avatarDisplaySrc, contactLatestAvatarUrl, displayName, filterContacts} from '@/lib/utils'
import {PageHeader} from '@/components/PageHeader'
import {ContactProfilePopover} from '@/components/ContactProfilePopover'

export function GroupMembersView({groups, account}: {groups: Contact[]; account: Account | null}) {
    const [selectedWxid, setSelectedWxid] = useState('')
    const [groupQuery, setGroupQuery] = useState('')
    const [memberQuery, setMemberQuery] = useState('')
    const [members, setMembers] = useState<Contact[]>([])
    const [loading, setLoading] = useState(false)
    const [profileMember, setProfileMember] = useState<Contact | null>(null)
    const [profileAnchor, setProfileAnchor] = useState<DOMRect | null>(null)
    const selected = groups.find((group) => group.wxid === selectedWxid) || groups[0] || null
    const filteredGroups = useMemo(() => filterContacts(groups, groupQuery), [groups, groupQuery])
    const filteredMembers = useMemo(() => filterContacts(members, memberQuery), [members, memberQuery])

    const loadMembers = useCallback(
        async (group = selected) => {
            if (!account || !group) return
            setLoading(true)
            try {
                const response = await apiPost<{success: boolean; members: Contact[]}>(
                    '/group-members',
                    {wxid: account.wxid, ChatRoomName: group.wxid},
                )
                setMembers(response.members || [])
            } finally {
                setLoading(false)
            }
        },
        [account, selected],
    )

    useEffect(() => {
        setMembers([])
        setMemberQuery('')
        void loadMembers(selected)
    }, [loadMembers, selected])

    const isOwner = (member: Contact) => {
        const ownerWxid = (member as Contact & {ownerWxid?: string}).ownerWxid
        return !!ownerWxid && member.wxid === ownerWxid
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="群成员列表"/>
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                <div className="w-full md:w-[298px] shrink-0 bg-white rounded-card shadow-card border border-hair flex flex-col overflow-hidden h-auto md:h-full">
                    <div className="p-4 border-b border-hair bg-gray-50 font-medium text-ink shrink-0">管理群成员</div>
                    <div className="p-3 border-b border-hair bg-page shrink-0">
                        <input
                            type="text"
                            value={groupQuery}
                            onChange={(event) => setGroupQuery(event.target.value)}
                            placeholder="搜索群聊"
                            className="w-full h-8 bg-white border border-hair rounded px-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filteredGroups.length ? (
                            filteredGroups.map((group) => {
                                const isActive = selected?.wxid === group.wxid
                                return (
                                    <button
                                        type="button"
                                        key={group.wxid}
                                        onClick={() => setSelectedWxid(group.wxid)}
                                        className={`flex items-center gap-3 p-3 w-full text-left border-b border-hair/40 transition-colors ${
                                            isActive ? 'bg-subActive text-ink' : 'text-soft hover:bg-subHover'
                                        }`}
                                    >
                                        <img
                                            src={avatarDisplaySrc(group.avatar, group.wxid)}
                                            alt={displayName(group)}
                                            className="w-10 h-10 rounded bg-gray-100 shrink-0"
                                        />
                                        <span className="text-sm truncate">{displayName(group)}</span>
                                    </button>
                                )
                            })
                        ) : (
                            <div className="text-center text-sm text-muted py-10">{groupQuery ? '没有匹配的群聊' : '暂无群聊'}</div>
                        )}
                    </div>
                </div>
                <div className="flex-1 min-h-[320px] md:min-h-0 bg-white rounded-card shadow-card border border-hair p-4 md:p-5 flex flex-col h-auto md:h-full overflow-hidden">
                    <div className="mb-6 shrink-0 border-b border-hair pb-4 flex justify-between items-center gap-3">
                        <h2 className="text-base font-medium text-ink truncate">
                            {selected ? `${displayName(selected)} (成员 ${filteredMembers.length} 人)` : '群成员'}
                        </h2>
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                                <input
                                    type="text"
                                    value={memberQuery}
                                    onChange={(event) => setMemberQuery(event.target.value)}
                                    placeholder="搜索成员"
                                    className="w-64 h-8 bg-gray-50 border border-hair rounded pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                                />
                            </div>
                            <button
                                type="button"
                                className="btn-secondary"
                                disabled={loading || !selected}
                                onClick={() => loadMembers()}
                            >
                                <RefreshCw size={16} className={loading ? 'animate-spin' : ''}/>
                                刷新
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0 pt-2">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-full text-muted gap-2">
                                <RefreshCw size={34} className="animate-spin"/>
                                <span className="text-sm">正在获取群成员</span>
                            </div>
                        ) : filteredMembers.length ? (
                            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8">
                                {filteredMembers.map((member) => (
                                    <div
                                        key={member.wxid}
                                        className="flex flex-col items-center group cursor-pointer"
                                        onClick={(e) => {
                                            const el = e.currentTarget.querySelector('img') as HTMLElement | null
                                            setProfileMember(member)
                                            setProfileAnchor(el?.getBoundingClientRect() || null)
                                        }}
                                    >
                                        <div className="relative">
                                            <img
                                                src={avatarDisplaySrc(member.avatar, member.wxid)}
                                                alt={displayName(member)}
                                                className="h-14 w-14 shrink-0 rounded bg-gray-100 mb-2 group-hover:shadow-md group-hover:ring-2 group-hover:ring-accent/30 transition-all"
                                            />
                                            {isOwner(member) ? (
                                                <span className="absolute -top-2 -right-2 bg-accent text-white text-[10px] px-1.5 py-0.5 rounded-full border border-white">
                                                    群主
                                                </span>
                                            ) : null}
                                        </div>
                                        <span className="text-xs text-soft truncate w-full text-center">
                                            {displayName(member)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-muted gap-3">
                                <Users size={40}/>
                                <span className="text-sm">
                                    {selected
                                        ? memberQuery
                                            ? '没有匹配的成员'
                                            : '暂无群成员'
                                        : '请选择一个群聊'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Contact profile popover */}
            {profileMember && (
                <ContactProfilePopover
                    anchorRect={profileAnchor}
                    avatar={avatarDisplaySrc(profileMember.avatar, profileMember.wxid)}
                    latestAvatarUrl={contactLatestAvatarUrl(profileMember)}
                    displayName={displayName(profileMember)}
                    wxid={profileMember.wxid}
                    remark={profileMember.remark}
                    nickName={profileMember.nickName}
                    alias={profileMember.alias}
                    region={[profileMember.province, profileMember.city].filter(Boolean).join(' ') || undefined}
                    signature={profileMember.signature}
                    sex={profileMember.sex}
                    onClose={() => { setProfileMember(null); setProfileAnchor(null) }}
                />
            )}
        </section>
    )
}
